import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router";
import { createLookupTable, type LookupTableSummary } from "./api/client";
import { useLookupTables } from "../hooks/useLookupTables";
import { formatDate } from "./utils";
import { AdminListHeader } from "../../../components/admin/AdminListHeader";
import "../admin.css";
import "./LookupTableListPage.css";

// --- Create Dialog ---

interface CreateLookupTableDialogProps {
  onClose: () => void;
  onCreated: (table: LookupTableSummary) => void;
}

function CreateLookupTableDialog({ onClose, onCreated }: CreateLookupTableDialogProps) {
  const [name, setName] = useState("");
  const [columnsRaw, setColumnsRaw] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("Name is required.");
      return;
    }
    const columns = columnsRaw
      .split(",")
      .map((c) => c.trim())
      .filter((c) => c.length > 0);

    try {
      setIsSubmitting(true);
      setError(null);
      const table = await createLookupTable({ name: trimmedName, columns });
      onCreated(table);
    } catch (err) {
      setError((err as Error).message ?? "Failed to create lookup table.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Close on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="lt-dialog-backdrop" onClick={onClose}>
      <div className="lt-dialog" onClick={(e) => e.stopPropagation()}>
        <h2 className="lt-dialog-title">Create Lookup Table</h2>
        <form onSubmit={handleSubmit}>
          <div className="lt-dialog-field">
            <label className="lt-dialog-label" htmlFor="lt-name">
              Name
            </label>
            <input
              id="lt-name"
              className="lt-dialog-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Vendors"
              autoFocus
              required
            />
          </div>
          <div className="lt-dialog-field">
            <label className="lt-dialog-label" htmlFor="lt-columns">
              Columns{" "}
              <span className="lt-dialog-hint">(comma-separated, optional)</span>
            </label>
            <input
              id="lt-columns"
              className="lt-dialog-input"
              type="text"
              value={columnsRaw}
              onChange={(e) => setColumnsRaw(e.target.value)}
              placeholder="e.g. id, name, address"
            />
          </div>
          {error && <div className="lt-dialog-error">{error}</div>}
          <div className="lt-dialog-footer">
            <button
              type="button"
              className="rb-btn rb-btn-cancel"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rb-btn rb-btn-save"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Creating…" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// --- Page ---

export function LookupTableListPage() {
  const { tables, isLoading, error } = useLookupTables();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  const lowerSearch = search.trim().toLowerCase();
  const filtered = lowerSearch
    ? tables.filter((t) => t.name.toLowerCase().includes(lowerSearch))
    : tables;

  const handleCreated = (table: LookupTableSummary) => {
    setShowCreate(false);
    // R-109 — land on the new table's detail page (the natural next action
    // is configuring the table you just created), not back on the list.
    navigate(`/admin/lookup-tables/${table.id}`);
  };

  return (
    <div className="admin-page">
      <AdminListHeader
        title="Lookup Tables"
        count={tables.length}
        primaryAction={{ label: "+ Create Lookup Table", onClick: () => setShowCreate(true) }}
      />
      <div className="jt-header-actions" style={{ padding: "0 16px 8px" }}>
        <input
          className="jt-search"
          type="text"
          placeholder="Search lookup tables..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="jt-list">
        {isLoading && (
          <div className="admin-loading">
            <div className="loading-spinner" />
          </div>
        )}

        {error && <div className="admin-error">Error: {error}</div>}

        {!isLoading && !error && filtered.length === 0 && (
          search.trim() ? (
            <div className="jt-empty">
              No lookup tables match &ldquo;{search}&rdquo;.
            </div>
          ) : (
            <div className="tab-placeholder">
              <span className="tab-placeholder-text">
                No lookup tables yet. Lookup tables let operators validate field values against a shared list.
              </span>
              <button
                type="button"
                className="rb-btn rb-btn-save"
                style={{ marginTop: "var(--space-4)" }}
                onClick={() => setShowCreate(true)}
              >
                Create one
              </button>
            </div>
          )
        )}

        {filtered.map((t) => (
          <div key={t.id} className="jt-card">
            <div className="jt-card-header">
              <span className="jt-card-name">{t.name}</span>
            </div>
            <div className="jt-card-meta">
              <span>{t.columns.length} column{t.columns.length === 1 ? "" : "s"}</span>
              <span>&middot;</span>
              <span>{t.entryCount} {t.entryCount === 1 ? "entry" : "entries"}</span>
              <span>&middot;</span>
              <span>Updated {formatDate(t.updatedAt)}</span>
            </div>
            <div className="jt-card-actions">
              <Link
                to={`/admin/lookup-tables/${t.id}`}
                className="jt-action-btn"
              >
                View &rarr;
              </Link>
            </div>
          </div>
        ))}
      </div>

      {showCreate && (
        <CreateLookupTableDialog
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
}
